<template>
    <div
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
        role="dialog"
        @click.self="$emit('close')"
    >
        <div class="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-auto">
            <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                <h3 class="text-lg font-semibold text-slate-700 dark:text-white">
                    Revalidação biométrica do usuário
                </h3>
                <button
                    type="button"
                    class="text-slate-400 hover:text-slate-600 dark:hover:text-white"
                    @click="$emit('close')"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <div class="px-6 py-4 space-y-6 text-sm text-slate-600 dark:text-slate-300">
                <div v-if="loading" class="flex items-center justify-center py-8">
                    <svg class="w-8 h-8 text-indigo-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                </div>

                <template v-else-if="data">
                    <idwall-pending-flows-alert
                        v-if="data.idwall_pending_flows?.length"
                        :flows="data.idwall_pending_flows"
                    />

                    <section v-if="data.front_visibility">
                        <div :class="summaryCardClass(data.front_visibility.ui_shown)" class="rounded-md p-4 border">
                            <p class="text-xs uppercase tracking-wide font-semibold opacity-70 mb-1">
                                O que esse usuário está vendo agora
                            </p>
                            <p class="text-lg font-semibold leading-snug">
                                {{ data.front_visibility.summary }}
                            </p>
                            <p class="text-sm mt-2 opacity-90">
                                {{ data.front_visibility.explanation }}
                            </p>
                            <p class="text-xs mt-3 italic opacity-80">
                                <strong>Próximo passo:</strong> {{ data.front_visibility.next_action }}
                            </p>
                        </div>
                    </section>

                    <section>
                        <h4 class="font-semibold text-slate-700 dark:text-white mb-2">Usuário</h4>
                        <dl class="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                            <dt class="text-slate-400">Nome</dt>
                            <dd>{{ data.name }}</dd>
                            <dt class="text-slate-400">Email</dt>
                            <dd>{{ data.email }}</dd>
                            <dt class="text-slate-400">Tipo</dt>
                            <dd>{{ data.type === 'company' ? 'PJ' : 'PF' }}</dd>
                            <dt class="text-slate-400">CPF</dt>
                            <dd>{{ data.cpf || '—' }}</dd>
                            <dt class="text-slate-400">CNPJ</dt>
                            <dd>{{ data.cnpj || '—' }}</dd>
                            <dt class="text-slate-400">Status do cadastro</dt>
                            <dd>{{ statusLabel(data.user_status) }}</dd>
                        </dl>
                    </section>

                    <section>
                        <h4 class="font-semibold text-slate-700 dark:text-white mb-2">Dados que serão enviados pra IdWall</h4>
                        <ul class="space-y-1 text-sm">
                            <li :class="data.data_check.effective_cpf ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                                [{{ data.data_check.effective_cpf ? 'OK' : 'Faltando' }}]
                                CPF: {{ data.data_check.effective_cpf || '—' }}
                                <span v-if="data.data_check.cpf_source" class="text-slate-400 ml-1 text-xs">
                                    (origem: {{ sourceLabel(data.data_check.cpf_source) }})
                                </span>
                            </li>
                            <li :class="data.data_check.effective_birth ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                                [{{ data.data_check.effective_birth ? 'OK' : 'Faltando' }}]
                                Data de nascimento: {{ formatBirth(data.data_check.effective_birth) || '—' }}
                                <span v-if="data.data_check.birth_source" class="text-slate-400 ml-1 text-xs">
                                    (origem: {{ sourceLabel(data.data_check.birth_source) }})
                                </span>
                            </li>
                            <li :class="data.data_check.effective_name ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                                [{{ data.data_check.effective_name ? 'OK' : 'Faltando' }}]
                                Nome: {{ data.data_check.effective_name || '—' }}
                            </li>
                        </ul>
                    </section>

                    <section>
                        <h4 class="font-semibold text-slate-700 dark:text-white mb-2">
                            Histórico de tentativas
                            <span v-if="data.attempt_history" class="text-slate-400 font-normal text-xs ml-1">
                                ({{ data.attempt_history.length }} {{ data.attempt_history.length === 1 ? 'tentativa' : 'tentativas' }})
                            </span>
                        </h4>
                        <div v-if="!data.attempt_history || !data.attempt_history.length" class="text-xs text-slate-400 italic">
                            Esse usuário ainda não fez nenhuma tentativa de revalidação biométrica.
                        </div>
                        <div v-else class="space-y-2">
                            <div
                                v-for="(req, idx) in data.attempt_history"
                                :key="req.id"
                                class="rounded border border-slate-200 dark:border-slate-700 p-2 text-xs"
                            >
                                <div class="flex items-center justify-between">
                                    <span class="font-medium text-slate-600 dark:text-slate-300">
                                        Tentativa #{{ data.attempt_history.length - idx }}
                                        <span class="text-slate-400 font-normal">(id {{ req.id }})</span>
                                    </span>
                                    <span :class="attemptStatusBadge(req.status)" class="rounded-full px-2 py-0.5 text-xs font-medium">
                                        {{ req.status_label }}
                                    </span>
                                </div>
                                <p class="text-slate-500 dark:text-slate-400 mt-1">
                                    Iniciada em {{ formatDate(req.created_at) }}
                                    <span v-if="req.completed_at">
                                        — finalizada em {{ formatDate(req.completed_at) }}
                                        <span v-if="req.completion_source === 'manual_cleanup'" class="italic">
                                            (encerrada manualmente)
                                        </span>
                                        <span v-else-if="req.completion_source === 'auto_timeout'" class="italic">
                                            (encerrada pelo timeout automático)
                                        </span>
                                        <span v-else-if="req.completion_source === 'orphan_cleanup'" class="italic">
                                            (encerrada pelo cleanup automático)
                                        </span>
                                    </span>
                                </p>
                            </div>
                        </div>
                    </section>

                    <section>
                        <h4 class="font-semibold text-slate-700 dark:text-white mb-2">
                            Status dos webhooks IdWall
                        </h4>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <div
                                v-for="webhook in webhookStatusCards"
                                :key="webhook.key"
                                class="rounded border border-slate-200 dark:border-slate-700 p-2 text-xs"
                            >
                                <div class="flex items-center justify-between mb-1">
                                    <span class="font-medium text-slate-600 dark:text-slate-300">
                                        {{ webhook.label }}
                                    </span>
                                    <span
                                        :class="webhook.data?.received
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                            : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'"
                                        class="rounded-full px-2 py-0.5 text-xs font-medium"
                                    >
                                        {{ webhook.data?.received ? 'Recebido' : 'Aguardando' }}
                                    </span>
                                </div>
                                <div v-if="webhook.data?.received" class="text-slate-500 dark:text-slate-400 space-y-0.5">
                                    <p>
                                        Status: <span class="font-medium">{{ webhook.data.auto_processed_status || '—' }}</span>
                                    </p>
                                    <p v-if="webhook.data.auto_processed_at">
                                        Em: {{ formatDate(webhook.data.auto_processed_at) }}
                                    </p>
                                    <p v-if="webhook.data.kyc_process_id" class="text-slate-400">
                                        kyc_process #{{ webhook.data.kyc_process_id }}
                                    </p>
                                    <button
                                        v-if="webhook.data.payload"
                                        type="button"
                                        class="mt-1 text-blue-600 dark:text-blue-300 hover:underline"
                                        @click="togglePayload(webhook.key)"
                                    >
                                        {{ expandedPayloads[webhook.key] ? 'Esconder payload' : 'Ver payload' }}
                                    </button>
                                    <pre
                                        v-if="webhook.data.payload && expandedPayloads[webhook.key]"
                                        class="mt-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded p-2 text-[10px] overflow-auto max-h-64 whitespace-pre-wrap"
                                    >{{ JSON.stringify(webhook.data.payload, null, 2) }}</pre>
                                </div>
                                <p v-else class="text-slate-400 italic">
                                    Nenhum webhook recebido ainda para esse fluxo.
                                </p>
                            </div>
                        </div>
                    </section>

                    <section>
                        <h4 class="font-semibold text-slate-700 dark:text-white mb-2">
                            Histórico de validações na IdWall
                            <span v-if="data.existing_facematch_processes" class="text-slate-400 font-normal text-xs ml-1">
                                ({{ data.existing_facematch_processes.length }} {{ data.existing_facematch_processes.length === 1 ? 'validação' : 'validações' }})
                            </span>
                        </h4>
                        <div v-if="!data.existing_facematch_processes || !data.existing_facematch_processes.length" class="text-xs text-slate-400 italic">
                            Esse usuário ainda não possui nenhuma validação biométrica aprovada na IdWall.
                        </div>
                        <div v-else class="space-y-2">
                            <div
                                v-for="kp in data.existing_facematch_processes"
                                :key="kp.id"
                                class="rounded border border-slate-200 dark:border-slate-700 p-2 text-xs"
                            >
                                <div class="flex items-center justify-between">
                                    <span class="font-medium text-slate-600 dark:text-slate-300">
                                        {{ kycProcessTypeLabel(kp.type) }}
                                        <span class="text-slate-400 font-normal">(id {{ kp.id }})</span>
                                    </span>
                                    <span class="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full px-2 py-0.5 text-xs font-medium">
                                        {{ kp.auto_processed_status }}
                                    </span>
                                </div>
                                <p class="text-slate-500 dark:text-slate-400 mt-1">
                                    Criado em {{ formatDate(kp.created_at) }}
                                    <span v-if="kp.auto_processed_at">
                                        — aprovado em {{ formatDate(kp.auto_processed_at) }}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </section>

                    <section v-if="data.existing_facematch_processes && data.existing_facematch_processes.length && data.can_force_overlay">
                        <div class="rounded-md bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 px-3 py-2 text-yellow-800 dark:text-yellow-200 text-xs">
                            <strong>Atenção antes de forçar overlay:</strong>
                            esse usuário já possui {{ data.existing_facematch_processes.length }} validação(ões) biométrica(s) aprovada(s) pela IdWall.
                            Forçar overlay vai gerar nova cobrança e novo processo facematch — confirme com o time se realmente é necessário.
                        </div>
                    </section>

                    <section v-if="data.force_overlay_blockers && data.force_overlay_blockers.length">
                        <div class="rounded-md bg-yellow-50 dark:bg-yellow-900/20 px-3 py-2 text-yellow-700 dark:text-yellow-200 text-xs">
                            <strong>Não é possível forçar overlay agora:</strong>
                            <ul class="list-disc ml-4 mt-1">
                                <li v-for="reason in data.force_overlay_blockers" :key="reason">
                                    {{ blockerLabel(reason) }}
                                </li>
                            </ul>
                        </div>
                    </section>

                    <section>
                        <button
                            type="button"
                            class="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                            @click="showTechnical = !showTechnical"
                        >
                            {{ showTechnical ? 'Esconder' : 'Mostrar' }} detalhes técnicos
                        </button>

                        <div v-if="showTechnical" class="mt-2 space-y-3 rounded-md border border-slate-200 dark:border-slate-700 p-3 text-xs">
                            <div>
                                <p class="font-semibold text-slate-600 dark:text-slate-300 mb-1">Campos brutos do usuário</p>
                                <dl class="grid grid-cols-2 gap-x-4 gap-y-1 font-mono">
                                    <dt class="text-slate-400">biometric_required_at</dt>
                                    <dd>{{ formatDate(data.biometric_required_at) }}</dd>
                                    <dt class="text-slate-400">biometric_validated_at</dt>
                                    <dd>{{ formatDate(data.biometric_validated_at) }}</dd>
                                    <dt class="text-slate-400">biometric_block_reason</dt>
                                    <dd>{{ data.biometric_block_reason || '—' }}</dd>
                                    <dt class="text-slate-400">biometric_attempts</dt>
                                    <dd>{{ data.biometric_attempts }}</dd>
                                    <dt class="text-slate-400">flow_state (campo legacy)</dt>
                                    <dd>{{ data.flow_state }}</dd>
                                </dl>
                            </div>

                            <div v-if="data.front_visibility">
                                <p class="font-semibold text-slate-600 dark:text-slate-300 mb-2">
                                    Condições avaliadas pelo front (hook <code>useBiometricBlocked</code>)
                                </p>

                                <p class="text-slate-500 dark:text-slate-400 mb-1">
                                    <strong>Obrigatórias</strong> (todas precisam ser <em>Sim</em>):
                                </p>
                                <ul class="space-y-0.5 font-mono mb-3">
                                    <li :class="data.front_visibility.conditions.enforcement_on ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                                        [{{ data.front_visibility.conditions.enforcement_on ? 'Sim' : 'Não' }}]
                                        Feature flag <code>biometric_revalidation_enforcement</code> ativa
                                    </li>
                                    <li :class="data.front_visibility.conditions.required_at_set ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                                        [{{ data.front_visibility.conditions.required_at_set ? 'Sim' : 'Não' }}]
                                        <code>biometric_required_at</code> preenchido
                                    </li>
                                    <li :class="data.front_visibility.conditions.not_validated ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                                        [{{ data.front_visibility.conditions.not_validated ? 'Sim' : 'Não' }}]
                                        <code>biometric_validated_at</code> vazio
                                    </li>
                                </ul>

                                <p class="text-slate-500 dark:text-slate-400 mb-1">
                                    <strong>Alternativas</strong> (pelo menos <em>uma</em> precisa ser <em>Sim</em>):
                                </p>
                                <ul class="space-y-0.5 font-mono mb-3">
                                    <li :class="!data.front_visibility.conditions.has_in_progress_request ? 'text-green-600 dark:text-green-400' : 'text-slate-500'">
                                        [{{ !data.front_visibility.conditions.has_in_progress_request ? 'Sim' : 'Não' }}]
                                        Sem <code>BiometricRevalidationRequest in_progress</code> aberta
                                    </li>
                                    <li :class="data.front_visibility.conditions.is_pending_correction ? 'text-green-600 dark:text-green-400' : 'text-slate-500'">
                                        [{{ data.front_visibility.conditions.is_pending_correction ? 'Sim' : 'Não' }}]
                                        Status do usuário é <code>onboarding-pending-correction</code>
                                    </li>
                                </ul>

                                <p class="text-slate-500 dark:text-slate-400 italic">
                                    <strong>Regra:</strong> overlay aparece quando todas as obrigatórias forem <em>Sim</em>
                                    E pelo menos uma das alternativas for <em>Sim</em>.
                                    Se as obrigatórias bater mas nenhuma alternativa bater (= já enviou e não está em correção),
                                    o usuário vê o banner "Estamos verificando" em vez do overlay.
                                </p>
                            </div>

                            <div v-if="data.front_visibility && data.front_visibility.in_progress_requests && data.front_visibility.in_progress_requests.length">
                                <p class="font-semibold text-slate-600 dark:text-slate-300 mb-1">Requests in_progress abertas</p>
                                <ul class="list-disc ml-4 font-mono">
                                    <li v-for="req in data.front_visibility.in_progress_requests" :key="req.id">
                                        id {{ req.id }} (tentativa {{ req.attempt }}, criada em {{ formatDate(req.created_at) }})
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </section>
                </template>
            </div>

            <div class="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-2">
                <button
                    type="button"
                    class="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white"
                    @click="$emit('close')"
                >
                    Fechar
                </button>
                <button
                    v-if="data"
                    type="button"
                    :disabled="!data.can_force_overlay || forcing"
                    :class="data.can_force_overlay ? 'bg-indigo-500 hover:bg-indigo-400 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'"
                    class="px-4 py-2 text-sm font-semibold rounded-md shadow"
                    @click="forceOverlay"
                >
                    <span v-if="forcing">Forçando...</span>
                    <span v-else>Forçar overlay</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import IdwallPendingFlowsAlert from '../../components/compliance/IdwallPendingFlowsAlert.vue';

export default {
    components: {
        IdwallPendingFlowsAlert,
    },
    props: {
        userId: { type: Number, required: true },
    },
    emits: ['close', 'forced'],
    data() {
        return {
            loading: false,
            forcing: false,
            showTechnical: false,
            data: null,
            expandedPayloads: {},
        };
    },
    computed: {
        webhookStatusCards() {
            return [
                { key: 'facematch', label: 'Facematch (RG/CNH)', data: this.data?.facematch_webhook },
                { key: 'qsa', label: 'QSA - PF com Bag', data: this.data?.qsa_webhook },
            ];
        },
    },
    mounted() {
        this.fetch();
    },
    methods: {
        fetch() {
            this.loading = true;
            axios.get(`/api/kyc/biometric-revalidation/${this.userId}`)
                .then(({ data }) => {
                    this.data = data.data;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        forceOverlay() {
            if (!this.data?.can_force_overlay || this.forcing) return;

            const confirmed = window.confirm(
                'Atenção: forçar overlay no meio de um processo em andamento vai cancelar a tentativa atual e gerar uma nova requisição para o usuário (O que pode gerar processos em duplicidade na IdWall).\n\nSó prossiga se tiver certeza de que esse é o caminho correto.\n\nDeseja continuar?'
            );

            if (!confirmed) return;

            this.forcing = true;
            axios.post(`/api/kyc/biometric-revalidation/${this.userId}/force-overlay`)
                .then(() => {
                    this.$toast?.success('Overlay forçado com sucesso.');
                    this.$emit('forced');
                })
                .catch((error) => {
                    const message = error?.response?.data?.message ?? 'Erro ao forçar overlay.';
                    this.$toast?.error(message);
                })
                .finally(() => {
                    this.forcing = false;
                });
        },
        summaryCardClass(state) {
            return {
                overlay: 'bg-indigo-50 border-indigo-200 text-indigo-900 dark:bg-indigo-900/30 dark:border-indigo-800 dark:text-indigo-100',
                under_review_card: 'bg-yellow-50 border-yellow-200 text-yellow-900 dark:bg-yellow-900/30 dark:border-yellow-800 dark:text-yellow-100',
                normal_form: 'bg-green-50 border-green-200 text-green-900 dark:bg-green-900/30 dark:border-green-800 dark:text-green-100',
            }[state] ?? 'bg-slate-50 border-slate-200 text-slate-900 dark:bg-slate-900/30';
        },
        attemptStatusBadge(status) {
            return {
                approved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
                inconclusive: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
                rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
                pending: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200',
            }[status] ?? 'bg-gray-100 text-gray-800';
        },
        statusLabel(status) {
            return {
                'onboarding-waiting': 'Aguardando preenchimento (onboarding-waiting)',
                'onboarding-pending': 'Em análise (onboarding-pending)',
                'onboarding-approved': 'Aprovado (onboarding-approved)',
                'onboarding-pending-correction': 'Pendente de correção (onboarding-pending-correction)',
                'onboarding-reproved': 'Reprovado (onboarding-reproved)',
                'approved': 'Aprovado (approved)',
                'reproved': 'Reprovado (reproved)',
                'pending-correction': 'Pendente de correção (pending-correction)',
                'pending': 'Pendente (pending)',
            }[status] ?? status ?? '—';
        },
        blockerLabel(reason) {
            return {
                feature_flag_off: 'A feature flag de revalidação biométrica está desativada.',
                already_validated: 'Esse usuário já completou a revalidação biométrica.',
            }[reason] ?? reason;
        },
        kycProcessTypeLabel(type) {
            return {
                onboarding_rg_facematch: 'RG + facematch',
                onboarding_cnh_facematch: 'CNH + facematch',
                biometric_validation: 'Validação biométrica',
                onboarding_qsa_pf_com_bag: 'QSA - PF com Bag',
            }[type] ?? type;
        },
        togglePayload(key) {
            this.expandedPayloads = {
                ...this.expandedPayloads,
                [key]: !this.expandedPayloads[key],
            };
        },
        sourceLabel(source) {
            return {
                'user.cpf': 'CPF do usuário',
                'user.birth': 'data de nascimento do usuário',
                'company_partner.cpf': 'CPF do sócio',
                'company_partner.birth': 'data de nascimento do sócio',
            }[source] ?? source;
        },
        formatDate(value) {
            if (!value) return '—';
            try {
                return new Date(value).toLocaleString('pt-BR');
            } catch {
                return value;
            }
        },
        formatBirth(value) {
            if (!value) return '';
            try {
                const isoDate = String(value).split('T')[0];
                const [y, m, d] = isoDate.split('-');
                if (!y || !m || !d) return value;
                return `${d}/${m}/${y}`;
            } catch {
                return value;
            }
        },
    },
};
</script>
